import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  getCompanyIntegrations,
  getInstagramAuthUrl,
  getInstagramStatus,
  disconnectInstagramAccount,
  getCompanyIntegrationsHealth,
  getIntegrationHealth,
  reconnectInstagramAccount,
} from '../api';
import { INTEGRATIONS_CATALOG } from '../components/Integrations/mockData';
import RequestDrawer from '../components/Integrations/RequestDrawer';
import { IntegrationLogo } from '../components/Integrations/IntegrationsGrid';
import { Button, TableSkeleton } from '@/components';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import toast from 'react-hot-toast';
import {
  DashboardPage,
  DashboardCard,
  DashboardTableWrapper,
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
} from '../components/PremiumDesign';
import { getHealthBadgeInfo } from '../components/Integrations/integrationHealthUtils';

export function InstagramBusinessDetailPage() {
  const navigate = useNavigate();
  const [requestDrawerOpen, setRequestDrawerOpen] = useState(false);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const app = INTEGRATIONS_CATALOG.messengers.find(
    (a) => a.id === 'instagram',
  ) || {
    id: 'instagram',
    name: 'Instagram Business',
    logoType: 'instagram',
    description:
      'Connect your Instagram account to manage DMs and lead automation.',
  };

  const {
    data: companyIntegrations = [],
    isLoading: isIntegrationsApiLoading,
  } = useQuery({
    queryKey: ['company-integrations', companyId],
    queryFn: () => getCompanyIntegrations(companyId),
    enabled: !!companyId,
  });

  const { data: instagramStatusData, refetch: refetchStatus } = useQuery({
    queryKey: ['instagram-status', companyId],
    queryFn: () => getInstagramStatus(companyId),
    enabled: !!companyId,
  });

  const {
    data: healthListRaw = [],
    isLoading: isHealthListLoading,
    isError: isHealthError,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    queryKey: ['company-integrations-health', companyId],
    queryFn: () => getCompanyIntegrationsHealth(companyId),
    enabled: !!companyId,
  });

  const isIntegrationsLoading = useDelayedSkeleton(isIntegrationsApiLoading);
  const isHealthLoading = useDelayedSkeleton(isHealthListLoading);

  const activeIntegration = companyIntegrations?.find(
    (item) => item.integration_slug === 'instagram',
  );

  const companyIntegrationId = activeIntegration?.id;

  const { data: singleHealthData } = useQuery({
    queryKey: ['integration-health-detail', companyId, companyIntegrationId],
    queryFn: () => getIntegrationHealth({ companyId, companyIntegrationId }),
    enabled: !!companyId && !!companyIntegrationId,
  });

  const resolvedIntegrationName =
    activeIntegration?.integration_name || app.name;
  const resolvedAppForDrawer = { ...app, name: resolvedIntegrationName };

  const isConnected =
    instagramStatusData?.is_connected ?? !!activeIntegration?.is_connected;

  const instagramHealthFromList = Array.isArray(healthListRaw)
    ? healthListRaw.find((item) => item.integration_slug === 'instagram')
    : null;

  const activeHealthItem = singleHealthData ||
    instagramHealthFromList || {
      id: companyIntegrationId || 1,
      integration_name: resolvedIntegrationName,
      integration_slug: 'instagram',
      lifecycle_status: isConnected ? 'activated' : 'pending',
      lifecycle_status_label: isConnected
        ? 'Account Activated'
        : 'Pending Setup',
      health_status: isConnected ? 'healthy' : 'disconnected',
      health_status_label: isConnected ? 'Healthy' : 'Not Connected',
    };

  const badgeInfo = getHealthBadgeInfo(
    activeHealthItem?.health_status,
    activeHealthItem?.health_status_label,
  );

  // Handle postMessage events from OAuth popup window if redirected
  useEffect(() => {
    const handleMessage = async (event) => {
      if (event.data?.type === 'INSTAGRAM_OAUTH_SUCCESS' && event.data?.code) {
        const loadingToast = toast.loading('Reconnecting Instagram...');
        try {
          await reconnectInstagramAccount({
            companyId,
            payload: {
              oauth_code: event.data.code,
              redirect_uri:
                window.location.origin + '/integrations/instagram/callback',
            },
          });
          toast.success('Instagram reconnected successfully!', {
            id: loadingToast,
          });
          refetchStatus();
          refetchHealth();
        } catch (err) {
          toast.error(
            err?.response?.data?.detail ||
              err?.message ||
              'Failed to complete reconnection',
            { id: loadingToast },
          );
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [companyId, refetchStatus, refetchHealth]);

  const handleReconnectMeta = async () => {
    if (!companyId) {
      toast.error('Company ID is missing');
      return;
    }

    const width = 600;
    const height = 700;
    const left = Math.max(0, Math.round((window.screen.width - width) / 2));
    const top = Math.max(0, Math.round((window.screen.height - height) / 2));
    const features = `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,resizable=yes,status=no,toolbar=no,menubar=no`;

    const popup = window.open('about:blank', 'InstagramAuthPopup', features);

    try {
      const res = await getInstagramAuthUrl(companyId);
      const authUrl =
        res?.url ||
        res?.auth_url ||
        res?.redirect_url ||
        res?.data?.url ||
        res?.data?.auth_url ||
        (typeof res === 'string' ? res : null);

      if (authUrl) {
        if (popup) {
          popup.location.href = authUrl;
          popup.focus();
        } else {
          window.location.href = authUrl;
        }
        refetchStatus();
        refetchHealth();
      } else {
        if (popup) popup.close();
        toast.error('Failed to retrieve Instagram authentication URL');
      }
    } catch (err) {
      if (popup) popup.close();
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to reconnect Instagram',
      );
    }
  };

  const handleDisconnectInstagram = async () => {
    if (!companyId) return;
    try {
      await disconnectInstagramAccount(companyId);
      toast.success('Instagram account disconnected successfully!');
      refetchStatus();
      refetchHealth();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          'Failed to disconnect Instagram account',
      );
    }
  };

  return (
    <DashboardPage>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/integrations')}
            className="w-8 h-8 rounded-md border border-gray-300 hover:bg-gray-50 text-gray-700 flex items-center justify-center transition-colors focus:outline-none shrink-0 bg-white shadow-sm"
            title="Back to Catalog"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 className="text-[22px] font-semibold text-gray-900 tracking-tight">
            {resolvedIntegrationName} Details
          </h1>
        </div>
      </div>

      <div className="w-full">
        {isIntegrationsLoading ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            <div className="xl:col-span-1 flex flex-col gap-6">
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <div className="flex items-center gap-3">
                  <div className="h-[52px] w-[52px] rounded-full bg-gray-100 animate-pulse" />
                  <div className="flex flex-col gap-2">
                    <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
                    <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                  </div>
                </div>
              </DashboardCard>
            </div>
            <div className="xl:col-span-2 min-w-0">
              <TableSkeleton columns={5} rows={3} />
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 items-start">
            {/* Left Column: Integration Card */}
            <div className="xl:col-span-1 flex flex-col gap-6">
              <DashboardCard className="flex flex-col gap-5 p-5 shadow-sm border border-gray-100 rounded-[16px] bg-white">
                <div className="flex items-center gap-3">
                  <IntegrationLogo
                    type={app.logoType}
                    size="h-[52px] w-[52px]"
                  />
                  <div className="flex flex-col items-start gap-1">
                    <h2 className="text-base font-semibold text-gray-900 leading-tight">
                      {resolvedIntegrationName}
                    </h2>
                    <span
                      className={`inline-flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${badgeInfo.className}`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${badgeInfo.dotColor}`}
                      />
                      <span>{badgeInfo.label}</span>
                    </span>
                  </div>
                </div>

                {activeHealthItem?.failure_reason && !badgeInfo.isHealthy && (
                  <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-start gap-2 leading-relaxed">
                    <svg
                      className="w-4 h-4 text-rose-500 shrink-0 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <div>
                      <strong className="font-semibold block mb-0.5">
                        Connection Alert
                      </strong>
                      <span>{activeHealthItem.failure_reason}</span>
                    </div>
                  </div>
                )}

                <div className="flex flex-col gap-4 text-sm mt-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm text-gray-400 font-medium">
                      Instagram Business
                    </span>
                    <span className="text-gray-900 text-base font-medium">
                      {activeHealthItem?.connected_account_name ||
                        instagramStatusData?.account_name ||
                        instagramStatusData?.username ||
                        activeIntegration?.config?.account_name ||
                        'Not Connected'}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-sm text-gray-400 font-medium">
                      Linked Meta Workspace
                    </span>
                    <span className="text-gray-900 text-base font-medium">
                      {activeHealthItem?.connected_workspace_name ||
                        instagramStatusData?.page_name ||
                        instagramStatusData?.business_name ||
                        activeIntegration?.config?.business_name ||
                        'Not Connected'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100 flex-wrap">
                  <button
                    onClick={handleReconnectMeta}
                    className="w-max bg-[#4CAF50] hover:bg-[#43a047] text-white text-[13px] font-medium py-2 px-4 rounded-md flex items-center gap-2 transition-colors shadow-sm"
                  >
                    <span>Connect / Reconnect</span>
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 5l7 7m0 0l-7 7m7-7H3"
                      />
                    </svg>
                  </button>
                  {instagramStatusData?.is_connected !== false && (
                    <button
                      onClick={handleDisconnectInstagram}
                      className="w-max bg-red-50 hover:bg-red-100 text-red-600 text-[13px] font-medium py-2 px-3 rounded-md transition-colors"
                    >
                      Disconnect
                    </button>
                  )}
                </div>
              </DashboardCard>
            </div>

            {/* Right Column: Details Table */}
            <div className="xl:col-span-2 min-w-0">
              {isHealthLoading ? (
                <TableSkeleton columns={5} rows={5} />
              ) : isHealthError ? (
                <div className="w-full p-8 bg-red-500/5 border border-red-500/10 rounded-2xl flex flex-col items-center justify-center gap-3 text-center">
                  <div className="text-red-500 text-sm font-semibold">
                    Failed to fetch integration health status
                  </div>
                  <p className="text-xs text-text-muted max-w-sm">
                    {healthError?.response?.data?.detail ||
                      healthError?.message ||
                      'Error occurred while contacting the health endpoint.'}
                  </p>
                  <Button
                    variant="primary"
                    onClick={() => refetchHealth()}
                    className="py-2 px-5 h-[38px] text-xs"
                  >
                    Retry Check
                  </Button>
                </div>
              ) : (
                <DashboardTableWrapper>
                  <DashboardTable className="w-full">
                    <DashboardTableHeader>
                      <th className="py-3 px-6 text-left font-medium">
                        Integration Name
                      </th>
                      <th className="py-3 px-6 text-left font-medium">
                        Integration Slug
                      </th>
                      <th className="py-3 px-6 text-center font-medium">
                        Lifecycle Status
                      </th>
                      <th className="py-3 px-6 text-center font-medium">
                        Health Status
                      </th>
                      <th className="py-3 px-6 text-center font-medium w-24">
                        Action
                      </th>
                    </DashboardTableHeader>
                    <DashboardTableBody>
                      {(() => {
                        const lifecycleLower =
                          activeHealthItem.lifecycle_status?.toLowerCase() ||
                          '';
                        const isLifecycleActivated =
                          lifecycleLower === 'activated';

                        return (
                          <DashboardTableRow className="border-b border-gray-50 last:border-none">
                            <td className="py-4 px-6 text-[13px] text-gray-900 font-medium">
                              {activeHealthItem.integration_name ||
                                resolvedIntegrationName}
                            </td>
                            <td className="py-4 px-6 text-[13px] text-gray-900">
                              {activeHealthItem.integration_slug || 'instagram'}
                            </td>
                            <td className="py-4 px-6 text-center">
                              <span
                                className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${
                                  isLifecycleActivated
                                    ? 'text-[#4CAF50] bg-[#E8F5E9]'
                                    : 'text-amber-600 bg-amber-50'
                                }`}
                              >
                                {activeHealthItem.lifecycle_status_label ||
                                  activeHealthItem.lifecycle_status ||
                                  'Account Activated'}
                              </span>
                            </td>
                            <td className="py-4 px-6 text-center">
                              <span
                                className={`inline-block px-3 py-1 text-[11px] font-semibold rounded ${badgeInfo.className}`}
                              >
                                {badgeInfo.label}
                              </span>
                            </td>
                            <td
                              className="py-4 px-6 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="flex items-center justify-center gap-3">
                                <button
                                  onClick={() =>
                                    navigate(
                                      '/integrations/instagram-business/history',
                                    )
                                  }
                                  title="View Health History Logs"
                                  className="text-orange-500 hover:opacity-70 transition-opacity p-1 rounded hover:bg-orange-50"
                                >
                                  <svg
                                    className="w-[18px] h-[18px]"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => setRequestDrawerOpen(true)}
                                  title="View / Submit Integration Reconnect Request"
                                  className="text-purple-500 hover:opacity-70 transition-opacity p-1 rounded hover:bg-purple-50"
                                >
                                  <svg
                                    className="w-[18px] h-[18px]"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </DashboardTableRow>
                        );
                      })()}
                    </DashboardTableBody>
                  </DashboardTable>
                </DashboardTableWrapper>
              )}
            </div>
          </div>
        )}
      </div>

      <RequestDrawer
        integration={resolvedAppForDrawer}
        companyIntegrationId={companyIntegrationId}
        isOpen={requestDrawerOpen}
        onClose={() => setRequestDrawerOpen(false)}
      />
    </DashboardPage>
  );
}
